import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';

export interface Poll<T> {
    data: T | undefined;
    error: string | undefined;
    loading: boolean;
    /** No answer yet, good or bad: the screen shows it is loading, not that there is nothing. */
    pending: boolean;
    /** When the last answer came in; drives the freshness labels. */
    loadedAt: Date | undefined;
    reload: () => void;
}

/** What went wrong with a call, in words for the screen. */
export const apiError = (e: unknown): string => {
    const any = e as { response?: { status?: number; data?: unknown }; message?: string };
    const status = any?.response?.status;
    const body = typeof any?.response?.data === 'string' ? any.response.data : '';

    // The local API with Auth0 bypassed: see LocalDevUserMiddleware on the server.
    if (status === 401 && body.includes('bypass only reads')) return 'De lokale API zonder inlog leest alleen. Log in met Auth0 om dit te doen.';
    if (status === 401 && body.includes('real Auth0 token')) return 'Dit leest live mee en vraagt een echte inlog, ook lokaal.';
    if (status === 401) return 'Niet ingelogd of sessie verlopen. Log opnieuw in.';
    if (status === 403) return 'Geen rechten voor dit onderdeel.';
    if (status === 404) return 'Deze API kent dit nog niet: hij draait een oudere build dan het dashboard.';
    return any?.message ?? String(e);
};

// How many polls are waiting for their first answer, for the bar under the top bar.
let firstLoads = 0;
const listeners = new Set<() => void>();
const bump = (delta: number) => {
    firstLoads = Math.max(0, firstLoads + delta);
    listeners.forEach((l) => l());
};
const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

/** How many parts of the screen are still loading for the first time. */
export const useFirstLoads = () => useSyncExternalStore(subscribe, () => firstLoads);

/** Counts a one-off call (a click, a dialog) in the same bar while it runs. */
export const track = async <T>(work: Promise<T>): Promise<T> => {
    bump(1);
    try {
        return await work;
    } finally {
        bump(-1);
    }
};

// The last answer per cache key, for as long as the tab is open.
const remembered = new Map<string, { data: unknown; at: Date }>();

// What one object is about (its dossier, timeline and live comparison) is also kept in the browser tab, so the
// object tabs show at once after a reload; it goes when the tab closes. The rest stays in memory only.
const LASTING = /^(dossier|timeline|live)\//;
const STORE_PREFIX = 'v2.cache.';

/** The last answer kept under a key: from memory, else from the browser tab for an object. */
export const recall = <T>(key: string): { data: T; at: Date } | undefined => {
    const hit = remembered.get(key);
    if (hit) return hit as { data: T; at: Date };
    if (!LASTING.test(key)) return undefined;
    try {
        const raw = sessionStorage.getItem(STORE_PREFIX + key);
        if (!raw) return undefined;
        const parsed = JSON.parse(raw) as { data: T; at: string };
        const entry = { data: parsed.data, at: new Date(parsed.at) };
        remembered.set(key, entry);
        return entry;
    } catch {
        return undefined;
    }
};

/** Keeps an answer under a key, for the next time the screen opens. */
export const remember = (key: string, data: unknown, at = new Date()) => {
    remembered.set(key, { data, at });
    if (!LASTING.test(key)) return;
    try {
        sessionStorage.setItem(STORE_PREFIX + key, JSON.stringify({ data, at: at.toISOString() }));
    } catch {
        // Full or not allowed: memory is enough for this session.
    }
};

/** Forgets what was kept for an object, when its tab closes. */
export const forget = (pattern: (key: string) => boolean) => {
    Array.from(remembered.keys()).filter(pattern).forEach((k) => remembered.delete(k));
    try {
        Object.keys(sessionStorage)
            .filter((k) => k.startsWith(STORE_PREFIX) && pattern(k.slice(STORE_PREFIX.length)))
            .forEach((k) => sessionStorage.removeItem(k));
    } catch {
        // Nothing kept there.
    }
};

/**
 * Loads something now and again every `everyMs` while the page is open, and keeps the last good
 * answer when a later one fails, so a hiccup does not blank the screen. Paused while the tab is
 * hidden; asks again as soon as it becomes visible.
 *
 * With a `cacheKey` a screen opened again shows its last answer at once and refreshes it behind it,
 * instead of starting empty.
 */
export const usePoll = <T>(
    load: ((signal: AbortSignal) => Promise<T>) | null,
    everyMs: number,
    deps: unknown[],
    cacheKey?: string
): Poll<T> => {
    const cached = cacheKey ? recall<T>(cacheKey) : undefined;
    const [data, setData] = useState<T | undefined>(cached?.data as T | undefined);
    const [error, setError] = useState<string>();
    const [loading, setLoading] = useState(false);
    const [loadedAt, setLoadedAt] = useState<Date | undefined>(cached?.at);
    const [nonce, setNonce] = useState(0);
    const loadRef = useRef(load);
    const keyRef = useRef(cacheKey);
    const answered = useRef(!!cached);
    loadRef.current = load;
    keyRef.current = cacheKey;

    const reload = useCallback(() => setNonce((n) => n + 1), []);

    useEffect(() => {
        const again = keyRef.current ? recall<T>(keyRef.current) : undefined;
        setData(again?.data as T | undefined);
        setError(undefined);
        setLoadedAt(again?.at);
        answered.current = !!again;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    useEffect(() => {
        if (!loadRef.current) return;

        let cancelled = false;
        let timer: ReturnType<typeof setTimeout> | undefined;
        let controller: AbortController | undefined;

        const run = async () => {
            if (cancelled || !loadRef.current) return;

            if (document.hidden) {
                timer = setTimeout(run, 1000);
                return;
            }

            controller = new AbortController();
            const first = !answered.current;
            if (first) bump(1);
            setLoading(true);

            try {
                const next = await loadRef.current(controller.signal);
                if (cancelled) return;
                answered.current = true;
                const at = new Date();
                if (keyRef.current) remember(keyRef.current, next, at);
                setData(next);
                setError(undefined);
                setLoadedAt(at);
            } catch (e) {
                if (cancelled || controller.signal.aborted) return;
                answered.current = true;
                setError(apiError(e));
            } finally {
                if (first) bump(-1);
                if (!cancelled) setLoading(false);
            }

            if (!cancelled && everyMs > 0) timer = setTimeout(run, everyMs);
        };

        run();

        return () => {
            cancelled = true;
            controller?.abort();
            if (timer) clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...deps, nonce, everyMs, load === null]);

    return { data, error, loading, pending: data === undefined && error === undefined && (loading || load !== null), loadedAt, reload };
};

/** A clock that ticks, for "x s geleden" and countdowns that move without new data. */
export const useNow = (everyMs = 1000): number => {
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), everyMs);
        return () => clearInterval(timer);
    }, [everyMs]);

    return now;
};
